import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {Subject, tap} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TranslationValue} from '../../generated/sales-terminal-client';
import {LanguageService} from '../../services/language/language.service';
import {AuthService} from '../../services/auth/auth.service';
import {UtilService} from '../../services/util/util.service';
import {cloneDeep} from 'lodash-es';
import {DirtyStateTrackerService} from '../../services/dirty-state-tracker/dirty-state-tracker.service';
import {ValidationService} from "../../services/validation/validation.service";
import {TextInputType} from '../forms/text-input/text-input.component';

export interface LanguageField {
  name: string;
  type: EditorType;
  required: (value?: any) => boolean;
  translations: TranslationValue[];
  label?: string;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  dirty?: boolean;
  parent?: string;
}

export enum EditorType {
  Text = 'text',
  TextArea = 'textarea',
  RichText = 'rich-text'
}

@Component({
  selector: 'app-lang-tabs',
  templateUrl: './lang-tabs.component.html',
  styleUrls: ['./lang-tabs.component.scss']
})
export class LangTabsComponent implements OnInit, OnDestroy, OnChanges {
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  public isLoading: boolean;
  @Input() public fields: LanguageField[] = [];
  @Input() public extendedFields: string[] = []; // Used by the component when disableSelection isn't enabled to clear additional tied fields on toggle
  @Input() public baseItem: Record<string, any>;
  @Input() public consumer: boolean; // Languages are initiated by another version of the component
  @Input() public primaryRequired: boolean = true;
  @Input() public disableCheckboxes: boolean;
  @Output() public valueChanges: EventEmitter<any> = new EventEmitter<any>();

  private _selectedLanguages: string[] = [];
  @Input() public set selectedLanguages(value: string[]) {
    this._selectedLanguages = value;
    if (!this._selectedLanguages?.length) {
      this.checkToInitLanguages();
    }
    this.selectedLanguagesChange.emit(this._selectedLanguages);
  }
  public get selectedLanguages(): string[] {
    return this._selectedLanguages || [];
  }
  @Output() public selectedLanguagesChange: EventEmitter<string[]> = new EventEmitter<string[]>();

  @Input() public dirtyService: DirtyStateTrackerService;
  @Input() public set dirtyTracker(value: any) {
    if (this.dirtyService) {
      this.fields.forEach(field => {
        field.dirty = this.dirtyService.isParentDirty(field.dirty, field.parent);
      });
    }
  }

  public get sortedLanguages(): string[] {
    return this.languageService.sortedWebShopLanguages || [];
  }

  constructor(public authService: AuthService,
              public utilService: UtilService,
              private languageService: LanguageService) {
  }

  public ngOnInit(): void {
    this.checkToInitLanguages();
  }
  private checkToInitLanguages(): void {
    this.isLoading = true;
    if (this.consumer) {
      if (this.languageService.sortedWebShopLanguages?.length) {
        this.initiateTranslations();
      }
      this.languageService.consumerListener.pipe(takeUntil(this.ngUnsubscribe)).subscribe(readyForConsumer => {
        if (readyForConsumer) {
          this.initiateTranslations();
        }
      });
    } else {
      this.initiateTranslations();
    }
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.fields) {
      this.initiateTranslations();
    }
  }

  public selectLanguage(index: number): void {
    if (this.consumer) { return; }
    if (!this.selectedLanguages.includes(this.sortedLanguages[index])) {
      this.selectedLanguages.push(this.sortedLanguages[index]);
    }
  }
  public toggleLanguage(language: string): void {
    if (this.selectedLanguages.includes(language)) {
      this.selectedLanguages.splice(this.selectedLanguages.indexOf(language), 1);
      this.fields.forEach(field => {
        this.baseItem[field.name].find((translation: TranslationValue) => translation.language === language).value = undefined;
      });
      this.extendedFields.forEach(field => {
        this.baseItem[field].find((translation: TranslationValue) => translation.language === language).value = undefined;
      })
    } else {
      this.selectedLanguages.push(language);
    }
  }

  private initiateTranslations(): void {
    if (this.consumer) {
      if (!this.selectedLanguages?.length) {
        return;
      }
      this.languageService.sortWebShopLanguages(this.selectedLanguages, this.authService.salesPointLanguage, true);
      this.initFields();
      this.isLoading = false;
    } else {
      this.languageService.getLanguages().pipe(
        tap({error: () => this.isLoading = false}),
        takeUntil(this.ngUnsubscribe)
      ).subscribe((languages: string[]) => {
        this.languageService.sortWebShopLanguages(languages, this.authService.salesPointLanguage);
        this.initFields();
        this.languageService.consumerListener.next(true);
        this.isLoading = false;
      });
    }
  }
  private initFields(): void {
    this.fields.forEach((field: LanguageField) => {
      this.updateLanguagesFromBaseValues(this.baseItem[field.name]);
      const updatedTranslations: TranslationValue[] = [];
      this.sortedLanguages.forEach((language) => {
        updatedTranslations.push({
          language: language,
          value: this.baseItem[field.name].find((translation: TranslationValue) => translation.language === language)?.value || ''
        });
      });
      this.baseItem[field.name] = cloneDeep(updatedTranslations);
      field.translations = this.baseItem[field.name];
    });
  }
  private updateLanguagesFromBaseValues(translationValues: TranslationValue[]): void {
    translationValues.forEach(translation => {
      if (translation.value && !this.selectedLanguages.includes(translation.language)) {
        this.selectedLanguages.push(translation.language);
      }
    });
  }

  public isValueValid(value: string, max: number): boolean {
    value = cloneDeep(ValidationService.getStringModelAsText(value));
    return !value?.length || value?.length <= max;
  }

  public getLangIndex(language: string): number {
    return this.sortedLanguages.indexOf(language);
  }

  public readonly EditorType = EditorType;
  protected readonly TextInputType = TextInputType;
}
